import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const POLL_MS = 30_000;

/** Minimal timeago — good enough for a dropdown. */
function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationBell({ compact = false }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* silent — user might be logging out */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
    load();
  };

  const markAllRead = async (e) => {
    e.stopPropagation();
    try { await api.post("/notifications/read-all"); load(); } catch { /* ignore */ }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={compact ? "notif-bell-mobile" : "notif-bell"}
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
        className={`relative inline-flex items-center justify-center transition-colors ${
          compact ? "flex-col text-[9px] text-zinc-500 hover:text-white" : "text-zinc-400 hover:text-white p-2"
        }`}
      >
        <Bell size={compact ? 22 : 20} weight={unread > 0 ? "fill" : "bold"} className={unread > 0 ? "text-[#FF5F1F]" : ""} />
        {compact && <span className="mt-0.5">Notify</span>}
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF5F1F] text-white text-[10px] font-mono font-bold flex items-center justify-center leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className={`absolute z-50 border border-white/15 bg-[#0A0A0A] shadow-2xl ${
            compact ? "bottom-full mb-2 right-0 w-80" : "top-full mt-2 right-0 w-96"
          }`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">
              → Notifications
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notif-mark-all-read"
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="notif-empty">
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openItem(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors hover:bg-white/5 ${
                    !n.read_at ? "bg-[#FF5F1F]/5" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!n.read_at && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FF5F1F] shrink-0" aria-hidden="true" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className={`font-display font-bold text-sm truncate ${!n.read_at ? "text-white" : "text-zinc-400"}`}>
                          {n.title}
                        </div>
                        <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest shrink-0">
                          {timeAgo(n.created_at)}
                        </div>
                      </div>
                      {n.body && (
                        <div className="mt-1 text-xs text-zinc-500 line-clamp-2 whitespace-pre-line">
                          {n.body}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
